import type { PropsWithChildren } from 'react';
import { NavLink } from 'react-router-dom';
import { getSignedInUserDisplayName, signOut } from '@shared/auth/cognito';
import { getRuntimeConfig } from '@shared/config/runtime';
import surelincLogo from '../assets/surelinc-logo.png';
import { AiAssistantProvider, useAiAssistant } from './AiAssistantDrawer';

function LayoutContent({ children }: PropsWithChildren) {
  const signedInDisplayName = getSignedInUserDisplayName('SURELINC admin');
  const { openDrawer, scope } = useAiAssistant();
  const { outreachTrackingEnabled } = getRuntimeConfig();
  const navItems = [
    { to: '/', label: 'Dashboard', end: true },
    { to: '/clients', label: 'Clients' },
    { to: '/controlled-evaluation-access', label: 'Controlled Access' },
    ...(outreachTrackingEnabled ? [{ to: '/outreach', label: 'Outreach' }] : []),
    { to: '/jobs', label: 'Jobs' },
    { to: '/evidence-packages', label: 'Evidence Packages' },
    { to: '/activity', label: 'Activity' },
  ];

  return (
    <div className="app-shell">
      <aside className="sidebar" aria-label="Admin portal navigation">
        <div className="brand">
          <div className="brand-logo-shell">
            <img className="brand-logo" src={surelincLogo} alt="SURELINC" />
          </div>
          <div className="brand-subtitle brand-subtitle-standalone">Readiness Platform Portal</div>
        </div>

        <nav className="nav-list">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => (isActive ? 'nav-link nav-link-active' : 'nav-link')}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
      </aside>

      <main className="main-content">
        <header className="topbar">
          <div>
            <h1>SURELINC Readiness Platform</h1>
            <p>Admin view for client registry, report jobs, and activity monitoring.</p>
          </div>
          <div className="user-actions">
            <button className="ai-topbar-button" type="button" onClick={() => openDrawer()} title={scope.clientId ? `AI Assistant scoped to ${scope.clientName || scope.clientId}` : 'Open AI Assistant'}>
              <span aria-hidden="true">✦</span> SURELINC Readiness AI Assistant
            </button>
            {signedInDisplayName && <span className="signed-in-user">{signedInDisplayName}</span>}
            <button className="secondary-button" type="button" onClick={signOut}>Sign out</button>
          </div>
        </header>

        <section className="page-content">{children}</section>
      </main>
    </div>
  );
}

export default function Layout({ children }: PropsWithChildren) {
  return (
    <AiAssistantProvider>
      <LayoutContent>{children}</LayoutContent>
    </AiAssistantProvider>
  );
}
